import React, { useState } from "react";
import PropTypes from "prop-types";
import Modal from "@/components/ui/modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  History,
  Copy,
  Check,
  Hash,
} from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { toast } from "sonner";

const TYPE_CONFIG = {
  receipt: { label: "Receipt", variant: "success" },
  sale: { label: "Sale", variant: "info" },
  adjustment: { label: "Stock Adjustment", variant: "warning" },
  transfer_in: { label: "Transfer In", variant: "success" },
  transfer_out: { label: "Transfer Out", variant: "destructive" },
  return: { label: "Return", variant: "secondary" },
  count: { label: "Stock Count", variant: "outline" },
};

const REFERENCE_LABELS = {
  goods_received_note_item: "Goods Received Note Item",
  goods_received_note: "Goods Received Note",
  sale_item: "Sale Item",
  sale: "Sale",
  stock_adjustment: "Stock Adjustment",
  stock_adjustment_item: "Stock Adjustment Item",
  repair_part: "Repair Part",
  stock_transfer: "Stock Transfer",
};

export const TransactionDetailsModal = ({
  open,
  onOpenChange,
  transaction,
  currencySymbol = "$",
}) => {
  const [copiedKey, setCopiedKey] = useState(null);

  if (!transaction) return null;

  const type = transaction.type || transaction.transaction_type || "adjustment";
  const typeConfig = TYPE_CONFIG[type] || {
    label: type?.replace(/_/g, " "),
    variant: "secondary",
  };

  const copyToClipboard = (text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    setCopiedKey(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const serialNumbers = transaction.meta?.serial_numbers || [];
  const oldQty = transaction.meta?.old_quantity;
  const newQty = transaction.meta?.new_quantity;
  const hasQtyTransition = oldQty !== undefined && newQty !== undefined;

  const handleCopyAllSerials = () => {
    if (!serialNumbers.length) return;
    navigator.clipboard.writeText(serialNumbers.join("\n"));
    toast.success(`Copied ${serialNumbers.length} serial numbers`);
  };

  const productName =
    transaction.inventory?.product?.name ||
    transaction.product?.name ||
    null;
  const productSku =
    transaction.inventory?.product?.sku ||
    transaction.product?.sku ||
    null;
  const storeName =
    transaction.inventory?.store?.name ||
    transaction.store?.name ||
    null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Transaction Details"
      icon={History}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-muted-foreground font-mono">
            ID: {transaction.id.substring(0, 8)}...
          </span>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {/* Top Summary Banner */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/40 border">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Movement Type
            </span>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={typeConfig.variant} className="capitalize font-medium">
                {typeConfig.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(transaction.created_at)}
              </span>
            </div>
          </div>

          <div className="text-right">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Quantity
            </span>
            <div
              className={`text-2xl font-black ${transaction.quantity > 0
                  ? "text-emerald-600"
                  : transaction.quantity < 0
                    ? "text-rose-600"
                    : "text-muted-foreground"
                }`}
            >
              {transaction.quantity > 0
                ? `+${transaction.quantity}`
                : transaction.quantity}
            </div>
            {hasQtyTransition && (
              <span className="text-xs text-muted-foreground font-mono">
                Stock: {oldQty} → {newQty}
              </span>
            )}
          </div>
        </div>

        {/* 2-Column Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card 1: Product & Inventory */}
          <Card>
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-semibold">
                Item Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 px-4 pb-4 text-sm">
              <div className="flex justify-between items-start gap-2">
                <span className="text-muted-foreground text-xs">Product:</span>
                <span className="font-medium text-foreground text-right text-xs">
                  {productName || "-"}
                </span>
              </div>

              {productSku && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">SKU:</span>
                  <Badge variant="outline" className="font-mono text-[11px]">
                    {productSku}
                  </Badge>
                </div>
              )}

              {storeName && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Store:</span>
                  <span className="font-medium text-foreground text-xs">{storeName}</span>
                </div>
              )}

              {transaction.inventory_id && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Inventory ID:</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(transaction.inventory_id, "inv")}
                    className="text-xs font-mono text-primary hover:underline flex items-center gap-1"
                    title="Copy Inventory ID"
                  >
                    {transaction.inventory_id.substring(0, 8)}...
                    {copiedKey === "inv" ? (
                      <Check className="h-3 w-3 text-emerald-600" />
                    ) : (
                      <Copy className="h-3 w-3 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Card 2: Transaction & Reference */}
          <Card>
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-semibold">
                Transaction Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 px-4 pb-4 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-xs">Reference:</span>
                <Badge variant="outline" className="text-xs capitalize font-medium">
                  {REFERENCE_LABELS[transaction.reference_type] ||
                    transaction.reference_type?.replace(/_/g, " ") ||
                    "Direct"}
                </Badge>
              </div>

              {transaction.reference_id && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Reference ID:</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(transaction.reference_id, "ref")}
                    className="text-xs font-mono text-primary hover:underline flex items-center gap-1"
                    title="Copy Reference ID"
                  >
                    {transaction.reference_id.substring(0, 8)}...
                    {copiedKey === "ref" ? (
                      <Check className="h-3 w-3 text-emerald-600" />
                    ) : (
                      <Copy className="h-3 w-3 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}

              {transaction.unit_cost !== null && transaction.unit_cost !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Unit Cost:</span>
                  <span className="font-medium text-xs">
                    {currencySymbol} {Number(transaction.unit_cost).toFixed(2)}
                  </span>
                </div>
              )}

              {transaction.total_value !== null && transaction.total_value !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Total Value:</span>
                  <span className="font-bold text-xs text-foreground">
                    {currencySymbol}{" "}
                    {Number(transaction.total_value).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              )}

              {transaction.meta?.adjustment_reason && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-xs">Reason:</span>
                  <span className="text-xs capitalize">
                    {transaction.meta.adjustment_reason.replace(/_/g, " ")}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Notes (if present) */}
        {transaction.notes && (
          <div className="p-3 rounded-lg border bg-muted/20 text-xs">
            <span className="text-muted-foreground font-semibold block mb-1">
              Notes
            </span>
            <p className="text-foreground italic">"{transaction.notes}"</p>
          </div>
        )}

        {/* Serials List (if attached) */}
        {serialNumbers.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-3 px-4 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                <Hash className="h-3.5 w-3.5 text-primary" />
                Serial Numbers ({serialNumbers.length})
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={handleCopyAllSerials}
              >
                <Copy className="h-3 w-3" /> Copy All
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <ScrollArea className="max-h-32">
                <div className="flex flex-wrap gap-1.5 p-1">
                  {serialNumbers.map((sn, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="font-mono text-xs cursor-pointer hover:bg-muted"
                      onClick={() => copyToClipboard(sn, `sn-${idx}`)}
                      title="Click to copy serial"
                    >
                      {sn}
                      {copiedKey === `sn-${idx}` && (
                        <Check className="h-3 w-3 ml-1 text-emerald-600 inline" />
                      )}
                    </Badge>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </Modal>
  );
};

TransactionDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  transaction: PropTypes.object,
  currencySymbol: PropTypes.string,
};

export default TransactionDetailsModal;
